import { z } from "zod";
import { billingRailSchema, moneySchema } from "./billing";
const timestampSchema = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const idSchema = z.string().trim().min(1).max(128);
const nullableIdSchema = idSchema.nullable();
const nullableTimestampSchema = timestampSchema.nullable();
const safeProductSnapshotSchema = z.discriminatedUnion("type", [
    z.strictObject({
        type: z.literal("access_pass"),
        name: z.string().trim().min(1).max(200),
        termMonths: z.number().int().min(1).max(12).optional(),
        monthlyInteractionQuota: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER).optional(),
        discountPercent: z.number().int().min(0).max(100).optional(),
    }),
    z.strictObject({
        type: z.literal("credit_pack"),
        name: z.string().trim().min(1).max(200),
        interactions: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER).optional(),
        expiresDays: z.number().int().min(1).max(3_650).optional(),
    }),
]);
export const accountExportSectionSchema = z.enum([
    "profile",
    "enrollment",
    "terms_acceptance",
    "guardian_relationship",
    "guardian_consent",
    "learning_progress",
    "development_path",
    "learning_event",
    "practice_session_summary",
    "assessment_summary",
    "billing_entitlement",
    "billing_usage",
    "billing_credit_lot",
    "billing_credit_balance",
    "billing_credit_ledger",
    "billing_payment",
    "billing_refund",
    "support_request",
    "linked_provider",
    "authorized_client",
    "achievement",
    "certificate",
]);
const availableSectionSchema = z.strictObject({
    section: accountExportSectionSchema.exclude(["achievement", "certificate"]),
    status: z.literal("available"),
    recordType: accountExportSectionSchema.exclude(["achievement", "certificate"]),
});
const unavailableSectionSchema = z.strictObject({
    section: z.enum(["achievement", "certificate"]),
    status: z.literal("unavailable"),
    reason: z.literal("backing_table_unavailable"),
});
export const accountExportManifestEnvelopeSchema = z
    .strictObject({
    type: z.literal("manifest"),
    schemaVersion: z.literal("account-export.v1"),
    generatedAt: timestampSchema,
    subject: z.strictObject({
        userId: idSchema,
        ownerId: nullableIdSchema,
    }),
    sections: z.array(z.union([availableSectionSchema, unavailableSectionSchema])).max(32),
})
    .superRefine((manifest, context) => {
    for (const [index, section] of manifest.sections.entries()) {
        if (section.status === "available" && section.section !== section.recordType) {
            context.addIssue({
                code: "custom",
                path: ["sections", index, "recordType"],
                message: "Available section recordType must match section",
            });
        }
    }
});
export const accountExportProfileRecordSchema = z.strictObject({
    user: z.strictObject({
        id: idSchema,
        name: z.string().trim().min(1).max(200),
        email: z.email().max(320),
        emailVerified: z.boolean(),
        createdAt: timestampSchema,
        updatedAt: timestampSchema,
    }),
    owner: z.strictObject({
        id: idSchema,
        preferredLocale: z.enum(["id", "en"]).nullable(),
        ageBand: z.enum(["adult", "minor", "unknown"]),
        status: z.enum(["pending", "active", "restricted", "deactivated"]),
        createdAt: timestampSchema,
        updatedAt: timestampSchema,
        deactivatedAt: nullableTimestampSchema,
    }),
});
export const accountExportEnrollmentRecordSchema = z.strictObject({
    id: idSchema,
    method: z.enum(["open", "invite_only", "approved_domain"]),
    status: z.enum(["pending", "active", "suspended", "revoked"]),
    sourceInvitationId: nullableIdSchema,
    approvedDomain: z.string().trim().min(1).max(253).nullable(),
    createdAt: timestampSchema,
    activatedAt: nullableTimestampSchema,
    suspendedAt: nullableTimestampSchema,
    revokedAt: nullableTimestampSchema,
});
export const accountExportTermsAcceptanceRecordSchema = z.strictObject({
    id: idSchema,
    policyId: idSchema,
    policyVersion: z.string().trim().min(1).max(100),
    policyHash: z.string().regex(/^[0-9a-f]{64}$/),
    renderedLocale: z.enum(["id", "en"]),
    acceptedAt: timestampSchema,
});
export const accountExportGuardianRelationshipRecordSchema = z.strictObject({
    id: idSchema,
    role: z.enum(["learner", "guardian"]),
    status: z.enum(["invited", "verified", "revoked"]),
    createdAt: timestampSchema,
    verifiedAt: nullableTimestampSchema,
    revokedAt: nullableTimestampSchema,
});
export const accountExportGuardianConsentRecordSchema = z.strictObject({
    id: idSchema,
    relationshipId: idSchema,
    role: z.enum(["learner", "guardian"]),
    policyVersion: z.string().trim().min(1).max(100),
    renderedLocale: z.enum(["id", "en"]),
    purposes: z.array(z.string().trim().min(1).max(100)).max(16),
    status: z.enum(["active", "revoked", "superseded"]),
    grantedAt: timestampSchema,
    revokedAt: nullableTimestampSchema,
});
export const accountExportLearningProgressRecordSchema = z.strictObject({
    topicId: idSchema,
    topicName: z.string().trim().min(1).max(200),
    collectionId: idSchema,
    collectionName: z.string().trim().min(1).max(200),
    locale: z.enum(["id", "en"]),
    difficulty: z.enum(["easy", "medium", "hard"]),
    masteryScore: z.number().int().min(0).max(100),
    masteryState: z.enum(["unseen", "learning", "practicing", "mastered"]),
    evidenceCount: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    lastEvidenceAt: nullableTimestampSchema,
    updatedAt: timestampSchema,
});
export const accountExportDevelopmentPathRecordSchema = z.strictObject({
    id: idSchema,
    version: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    status: z.enum(["draft", "active", "completed", "superseded"]),
    constitutionVersion: z.string().trim().min(1).max(128).nullable(),
    items: z
        .array(z.strictObject({
        topicId: idSchema,
        topicName: z.string().trim().min(1).max(200).nullable(),
        collectionName: z.string().trim().min(1).max(200).nullable(),
        reasonCode: z.string().trim().min(1).max(100),
        priority: z.number().int().min(0).max(1_000_000),
        nextAction: z.string().trim().min(1).max(100),
    }))
        .max(100),
    generatedFromEventId: nullableIdSchema,
    createdAt: timestampSchema,
    activatedAt: nullableTimestampSchema,
    completedAt: nullableTimestampSchema,
});
export const accountExportLearningEventRecordSchema = z.strictObject({
    id: idSchema,
    eventType: z.string().trim().min(1).max(100),
    entityType: z.string().trim().min(1).max(100),
    entityId: idSchema,
    interactionId: idSchema,
    topicId: nullableIdSchema,
    occurredAt: timestampSchema,
    createdAt: timestampSchema,
});
export const accountExportPracticeSessionSummaryRecordSchema = z.strictObject({
    id: idSchema,
    topicId: idSchema,
    interactionId: idSchema,
    status: z.enum(["created", "active", "completed", "abandoned", "expired"]),
    constitutionVersion: z.string().trim().min(1).max(128),
    createdAt: timestampSchema,
    startedAt: nullableTimestampSchema,
    completedAt: nullableTimestampSchema,
    expiresAt: timestampSchema,
});
export const accountExportAssessmentSummaryRecordSchema = z.strictObject({
    id: idSchema,
    practiceSessionId: idSchema,
    questionId: idSchema,
    evaluationSource: z.enum(["deterministic", "model_reported", "admin"]),
    score: z.number().int().min(0).max(100),
    confidence: z.number().min(0).max(1).nullable(),
    verified: z.boolean(),
    createdAt: timestampSchema,
});
export const accountExportBillingEntitlementRecordSchema = z.strictObject({
    id: idSchema,
    status: z.enum(["scheduled", "active", "expired", "revoked", "refunded"]),
    planId: idSchema,
    planName: z.string().trim().min(1).max(200),
    planPriceId: nullableIdSchema,
    paymentOrderId: nullableIdSchema,
    rail: z.union([
        billingRailSchema,
        z.strictObject({ provider: z.literal("fixture"), transport: z.literal("fixture") }),
    ]),
    startsAt: timestampSchema,
    endsAt: timestampSchema,
    quotaAnchorDay: z.number().int().min(1).max(31),
    termMonths: z.number().int().min(1).max(12),
    monthlyQuotaSnapshot: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    revokedAt: nullableTimestampSchema,
});
export const accountExportBillingUsageRecordSchema = z.strictObject({
    sourceType: z.enum(["free", "entitlement"]),
    sourceId: idSchema,
    periodStart: timestampSchema,
    periodEnd: timestampSchema,
    limitSnapshot: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    learningInteractionsUsed: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    version: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    updatedAt: timestampSchema,
});
export const accountExportBillingCreditLotRecordSchema = z.strictObject({
    id: idSchema,
    paymentOrderId: nullableIdSchema,
    originalAmount: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    remainingAmount: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    status: z.enum(["active", "depleted", "expired", "revoked"]),
    expiresAt: timestampSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const accountExportBillingCreditBalanceRecordSchema = z.strictObject({
    balance: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    version: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    updatedAt: timestampSchema,
});
export const accountExportBillingCreditLedgerRecordSchema = z.strictObject({
    id: idSchema,
    creditLotId: idSchema,
    paymentOrderId: nullableIdSchema,
    paymentRefundId: nullableIdSchema,
    delta: z.number().int().min(-Number.MAX_SAFE_INTEGER).max(Number.MAX_SAFE_INTEGER),
    reason: z.string().trim().min(1).max(100),
    sourceType: z.string().trim().min(1).max(100),
    createdAt: timestampSchema,
});
export const accountExportBillingPaymentRecordSchema = z.strictObject({
    id: idSchema,
    checkoutSessionId: idSchema,
    beneficiary: z.enum(["self", "guardian_beneficiary"]),
    status: z.enum([
        "pending",
        "paid",
        "partially_refunded",
        "refunded",
        "disputed",
        "chargeback",
        "failed",
        "expired",
    ]),
    rail: billingRailSchema,
    money: moneySchema,
    productSnapshot: safeProductSnapshotSchema,
    paidAt: nullableTimestampSchema,
    refundedAmount: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    effectType: z.enum(["entitlement", "credit_lot", "unfulfilled"]).nullable(),
    effectId: nullableIdSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const accountExportBillingRefundRecordSchema = z.strictObject({
    id: idSchema,
    paymentOrderId: idSchema,
    beneficiary: z.enum(["self", "guardian_beneficiary"]),
    status: z.enum(["creating", "provider_created", "pending", "succeeded", "failed", "cancelled"]),
    rail: billingRailSchema,
    money: moneySchema,
    triggerType: z.enum(["customer_request", "paid_capacity_conflict", "system", "provider"]),
    targetType: z.enum(["entitlement", "credit_lot", "unfulfilled"]),
    entitlementId: nullableIdSchema,
    creditLotId: nullableIdSchema,
    heldUnits: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    holdStatus: z.enum(["held", "released", "finalized"]),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    completedAt: nullableTimestampSchema,
});
export const accountExportSupportRequestRecordSchema = z.strictObject({
    id: idSchema,
    category: z.enum([
        "refund_request",
        "account_access",
        "billing",
        "privacy",
        "technical",
        "other",
    ]),
    subject: z.string().trim().min(1).max(200),
    message: z.string().trim().min(1).max(4_000),
    status: z.enum(["open", "resolved", "spam", "closed"]),
    paymentOrderId: nullableIdSchema,
    createdAt: timestampSchema,
    resolvedAt: nullableTimestampSchema,
});
export const accountExportLinkedProviderRecordSchema = z.strictObject({
    accountRowId: idSchema,
    provider: z.string().trim().min(1).max(64),
    label: z.string().trim().min(1).max(80),
    kind: z.enum(["password", "oauth"]),
    linkedAt: nullableTimestampSchema,
});
export const accountExportAuthorizedClientRecordSchema = z.strictObject({
    grantId: idSchema,
    clientId: z.string().trim().min(1).max(512),
    scopes: z.array(z.string().trim().min(1).max(100)).max(50),
    createdAt: z.union([timestampSchema, z.string().trim().min(1).max(100)]),
    expiresAt: z.union([timestampSchema, z.string().trim().min(1).max(100)]).optional(),
});
const accountExportRecordSchemaByType = {
    profile: accountExportProfileRecordSchema,
    enrollment: accountExportEnrollmentRecordSchema,
    terms_acceptance: accountExportTermsAcceptanceRecordSchema,
    guardian_relationship: accountExportGuardianRelationshipRecordSchema,
    guardian_consent: accountExportGuardianConsentRecordSchema,
    learning_progress: accountExportLearningProgressRecordSchema,
    development_path: accountExportDevelopmentPathRecordSchema,
    learning_event: accountExportLearningEventRecordSchema,
    practice_session_summary: accountExportPracticeSessionSummaryRecordSchema,
    assessment_summary: accountExportAssessmentSummaryRecordSchema,
    billing_entitlement: accountExportBillingEntitlementRecordSchema,
    billing_usage: accountExportBillingUsageRecordSchema,
    billing_credit_lot: accountExportBillingCreditLotRecordSchema,
    billing_credit_balance: accountExportBillingCreditBalanceRecordSchema,
    billing_credit_ledger: accountExportBillingCreditLedgerRecordSchema,
    billing_payment: accountExportBillingPaymentRecordSchema,
    billing_refund: accountExportBillingRefundRecordSchema,
    support_request: accountExportSupportRequestRecordSchema,
    linked_provider: accountExportLinkedProviderRecordSchema,
    authorized_client: accountExportAuthorizedClientRecordSchema,
};
const accountExportRecordEnvelopeSchemas = Object.entries(accountExportRecordSchemaByType).map(([recordType, schema]) => z.strictObject({
    type: z.literal("record"),
    section: z.literal(recordType),
    recordType: z.literal(recordType),
    record: schema,
}));
export const accountExportRecordEnvelopeSchema = z.union(accountExportRecordEnvelopeSchemas);
export const accountExportSectionEndEnvelopeSchema = z.strictObject({
    type: z.literal("section_end"),
    section: accountExportSectionSchema.exclude(["achievement", "certificate"]),
    recordCount: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
});
export const accountExportEndEnvelopeSchema = z.strictObject({
    type: z.literal("export_end"),
    generatedAt: timestampSchema,
    recordCount: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
});
export const accountExportEnvelopeSchema = z.union([
    accountExportManifestEnvelopeSchema,
    accountExportRecordEnvelopeSchema,
    accountExportSectionEndEnvelopeSchema,
    accountExportEndEnvelopeSchema,
]);
export const accountExportAvailableSections = Object.keys(accountExportRecordSchemaByType);
//# sourceMappingURL=account-export.js.map